// Skeleton-loader: shimmer-placeholder voor data-laden in plaats van "Laden…".
// CSS-driven (zie .skeleton + @keyframes skeleton-shimmer in global.css).

interface BlockProps {
  width?: string | number;
  height?: string | number;
  className?: string;
  style?: React.CSSProperties;
}

export function SkeletonBlock({ width, height, className = "", style }: BlockProps) {
  return (
    <div
      className={`skeleton ${className}`.trim()}
      style={{
        width: typeof width === "number" ? `${width}px` : width,
        height: typeof height === "number" ? `${height}px` : height,
        ...style,
      }}
    />
  );
}

/** Placeholder voor een KPI-card: label + value + delta-rij */
export function SkeletonKpi() {
  return (
    <div className="card kpi-card">
      <SkeletonBlock className="skeleton-block narrow" />
      <SkeletonBlock className="skeleton-block kpi-value" />
      <SkeletonBlock className="skeleton-block narrow" style={{ marginTop: 12, height: 12, width: "30%" }} />
    </div>
  );
}

/** Placeholder voor een tabel-rij: voor N rijen kun je SkeletonRows N keer renderen */
export function SkeletonRows({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div style={{ padding: "var(--space-3) var(--space-4)" }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: "var(--space-3)", marginBottom: "var(--space-2)" }}>
          {Array.from({ length: cols }).map((__, j) => (
            <SkeletonBlock key={j} className="skeleton-block wide" style={{ height: 14 }} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Placeholder voor card-content: header + 4-5 lijntjes */
export function SkeletonCard({ lines = 4 }: { lines?: number }) {
  return (
    <div className="card">
      <SkeletonBlock className="skeleton-block" style={{ height: 16, width: "40%", marginBottom: 12 }} />
      {Array.from({ length: lines }).map((_, i) => (
        <SkeletonBlock
          key={i}
          className="skeleton-block"
          style={{
            height: 12,
            width: i === lines - 1 ? "65%" : "100%",
            marginTop: 6,
          }}
        />
      ))}
    </div>
  );
}

/** Generieke Loading-fallback: shimmer-card centraal in de container, voor
 *  Suspense-fallbacks of bovenliggende module-loads. */
export function Loading({ message }: { message?: string }) {
  return (
    <div style={{
      padding: "var(--space-5)",
      display: "flex",
      flexDirection: "column",
      gap: "var(--space-3)",
      alignItems: "stretch",
    }}>
      <SkeletonCard lines={3} />
      {message && (
        <div style={{ color: "var(--color-text-subtle)", fontSize: "var(--fs-xs)", textAlign: "center", marginTop: 8 }}>
          {message}
        </div>
      )}
    </div>
  );
}
